import {
  ColumnFiltersState,
  GroupingState,
  SortingState
} from "@tanstack/react-table";
import { t } from "i18next";
import _ from "lodash";
import { DateTime, Duration } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { Column, RenderCellProps, RenderGroupCellProps, RenderHeaderCellProps, RenderSummaryCellProps } from "react-data-grid";
import { TbChartHistogram, TbCsv, TbX } from "react-icons/tb";
import { useAppStore } from "../../../stores/RootStore";
import {
  DataRow,
  Tab,
  useTabsStore
} from "../../../stores/TabsStore";
import { Button } from "../../../ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/Popover";
import { Separator } from "../../../ui/Separator";
import { HiddenFormatter, HiddenGroupFormatter, HiddenRenderer, SummaryRows } from "../../../ui/TableDataGrid/Renders";
import '../../../ui/TableDataGrid/styles.css';
import DeviceSensorDataInReport from "../../devices/deviceItemTabs/deviceNetworkTab/DeviceSensorDataInReport";

import Columns from "../filters/Columns";
import EquipmentsFilters from "../filters/EquipmentsFilters";
import Grouping from "../filters/Grouping";
import Sorting from "../filters/Sorting";

import { Event, Person } from "../../../types/types";
import { TableEventsHistogram } from "../../../ui/EChart/TableHistogram/TableEventsHistogram";
import { ReportTableDataGrid, getComparator } from "../../../ui/TableDataGrid/ReportTableDataGrid";
import { ReportViewButton } from "./ReportViewButton";



type Props = {
  tab: Tab;
};

export interface SummaryRow {
  id: string; 
  totalCount: number; 
  at: number;
  weekday: number;
  shift: number;
  equipment: number;
  equipment_model: number;
  equipment_type: number;
  equipment_division: number;
  availability: string;
  worked: string; 
  stopped: string; 
  turnedOff: string;
  unavailability: string;
  // personnel: number;
  // startWork: string;
  equipmentStartViolation: string;
  // endWork: string;
  equipmentEndViolation: string;
}

function TableView({ tab }: Props) {
  const {persons} = useAppStore()
  const tabStore = useTabsStore()
  const tableData = tab.options?.reportData as unknown as Array<DataRow>;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])
  const [viewableColumns, setViewableColumns] = useState<Column<DataRow, any>[]>([])
  const rows: DataRow[] = tableData;
  const [selectedRows, setSelectedRows] = useState((): ReadonlySet<number> => new Set());
  const [showGraph, setShowGrpah] = useState(false);
  const [showGraphForRow, setShowGrpahForRow] = useState<number | null>();


  function handleChangeShowGraph(rId: number) {
    if (!rId) {
      return;
    }
    if (rId === showGraphForRow) {
      setShowGrpah(false);
      setShowGrpahForRow(null);
    } else {
      setShowGrpah(true);
      setShowGrpahForRow(rId);
    }
  }

  function handleCloseShowGraph() {
    setShowGrpah(false);
    setShowGrpahForRow(null);
  }

  const columns = [
    {
      key: "hidden",
      name: "hidden",
      width: 60,
      frozen: true,
      resizable: false,
      sortable: false,
      renderHeaderCell(props: RenderHeaderCellProps<DataRow>) {
        return (
          <div className="flex items-center h-full gap-2 justify-end -mr-0.5">
            <HiddenRenderer {...props} />
          </div>
        );
      },
      renderCell(props: RenderCellProps<DataRow>) {
        return (
          <div className="flex items-center h-full gap-2 justify-between">
            <div
              className={`rounded-sm h-6 px-1 flex items-center ${
                showGraphForRow === props.row.id ? "bg-primary text-white" : "bg-muted text-foreground"
              }`}
            >
              <button type="button" onClick={() => handleChangeShowGraph(props.row.id)}>
                <TbChartHistogram size={16} />
              </button>
            </div>
            <HiddenFormatter {...props} />
          </div>
        );
      },
      renderGroupCell(props: RenderGroupCellProps<DataRow>) {
        return (
          <div className="flex items-center h-full gap-2 justify-end">
            <HiddenGroupFormatter {...props} />
          </div>
        );
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <>{p.row.totalCount}</>;
      },
    },
    {
      key: "at",
      name: t("at"),
      width: 100,
      renderCell: (p: any) => {
        return <>{(p.row as DataRow).dateShift.start_day.toFormat("dd.MM.yyyy")}</>;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{_.uniq(p.childRows.map((r) => r.dateShift.start_day.toFormat("dd.MM.yyyy"))).join(", ") as string}</span>
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
    },
    {
      key: "weekday",
      width: 80,
      name: t("weekday_short"),
      renderCell: (p: any) => {
        return <>{(p.row as DataRow).dateShift.start_day.weekdayShort?.toLocaleUpperCase()}</>;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{_.uniq(p.childRows.map((r) => r.dateShift.start_day.weekdayShort)).join(", ") as string}</span>
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
    },
    {
      key: "shift",
      name: t("shift"),
      //resizable: true,
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return <>{(p.row as DataRow).dateShift.label}</>;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{_.uniq(p.childRows.map((r) => r.dateShift.label)).join(", ") as string}</span>
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
    },
    {
      key: "division",
      name: t("division"),
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return <>{(p.row as DataRow).equipment.attributes.division_name}</>;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{_.uniq(p.childRows.map((r) => r.equipment.attributes.division_name)).join(", ") as string}</span>
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
    },
    {
      key: "inventory_number",
      name: t("inventory_number"),
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return <>{p.row.equipment.attributes.inventory_number}</>;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{_.uniq(p.childRows.map((r) => r.equipment.attributes.inventory_number)).join(", ") as string}</span>
      },
    },
    {
      key: "equipment",
      name: t("equipment_name"),
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return <>{(p.row as DataRow).equipment.attributes.name}</>;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{_.uniq(p.childRows.map((r) => r.equipment.attributes.name)).join(", ") as string}</span>
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
    },
    {
      key: "equipment_type",
      name: t("equipment_type"),
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return <>{(p.row as DataRow).equipment.attributes.equipment_type_name}</>;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{_.uniq(p.childRows.map((r) => r.equipment.attributes.equipment_type_name)).join(", ") as string}</span>
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
    },
    {
      key: "equipment_model",
      name: t("equipment_model_name"),
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return <>{p.row.equipment.attributes.equipment_model_name}</>;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{_.uniq(p.childRows.map((r) => r.equipment.attributes.equipment_model_name)).join(", ") as string}</span>
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
    },
    {
      key: "equipment_is_main",
      name: t("is_main"),
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return <>{p.row.equipment.attributes.is_main ? t('yes') : t('no')}</>;
      }
    },
    {
      key: "availability",
      name: t("availability"),
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return (
          <Popover>
            <PopoverTrigger className="hover:text-primary">
              {(p.row.availability.total.percent * 100).toFixed(2)}% ({p.row.availability.total.time})
            </PopoverTrigger>
            <PopoverContent align="start" alignOffset={-8} className="text-sm w-max ">
              <div className="flex flex-col space-y-2">
                <div className="space-x-2 flex justify-between">
                  <span>{t('total_availability')}:</span>
                  <span>{((p.row.availability.total.seconds / p.row.duration.total.seconds) * 100).toFixed(2)} ({p.row.availability.total.time})</span>
                </div>
                <div className="space-x-2 flex justify-between">
                  <span>{t('availability_on')}:</span>
                  <span>{(p.row.availability.on.percent * 100).toFixed(2)} ({Duration.fromObject({seconds: p.row.availability.on.seconds}).toFormat("hh:mm:ss")})</span>
                </div>
                <div className="space-x-2 flex justify-between">
                  <span>{t('availability_off')}:</span>
                  <span>{(p.row.availability.off.percent * 100).toFixed(2)} ({p.row.availability.off.time})</span>
                </div>
                <div className="space-x-2 flex justify-between">
                  <span>{t('interaction')}:</span>
                  <span>{(p.row.availability.interaction.percent * 100).toFixed(2)} ({p.row.availability.interaction.time})</span>
                </div>
              </div>
            </PopoverContent>
          </Popover>

        );
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{
          Duration.fromObject({ seconds: _.sum(p.childRows.map((r) => r.availability.total.seconds)) }).toFormat(
            "hh:mm:ss"
          ) as string
        }</span>
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
    },
    {
      key: "worked",
      name: t("worked"),
      width: 160,
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        let correctWithMaintenanceLoad = p.row.equipment.attributes.correct_maintenance_load
        return (
          <Popover>
            <PopoverTrigger className="hover:text-primary">
              {(p.row.availability.worked.percent * 100).toFixed(2)}% ({p.row.availability.worked.time})
            </PopoverTrigger>
            <PopoverContent align="start" alignOffset={-8} className="text-sm w-max ">
              <div className="flex flex-col space-y-2">
                <div className="space-y-1">
                  <div className="space-x-2 flex justify-between">
                    <span className="font-medium">{t('total_worked')}:</span>
                    <span>{(p.row.availability.worked.percent * 100).toFixed(2)} ({p.row.availability.worked.time})</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="space-x-2 flex justify-between">
                    <span className="font-medium">{t('worked')}:</span>
                    <span>{(p.row.works.worked.percent * 100).toFixed(2)} ({p.row.works.worked.time})</span>
                  </div>
                </div>
                <div className="space-y-1">
                  {correctWithMaintenanceLoad &&
                    <div className="space-x-2 flex justify-between">
                      <span className="font-medium">{t('maintenance_worked')}:</span>
                      <span>{(p.row.maintenance.worked.percent * 100).toFixed(2)} ({p.row.maintenance.worked.time})</span>
                    </div> 
                  }
                </div>
              </div>
            </PopoverContent>
          </Popover>
        );
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{(_.sum(p.childRows.map(r => r.availability.worked.percent * 100)) / p.childRows.length).toFixed(2)}% ({Duration.fromObject({seconds: _.sum(p.childRows.map(r => r.availability.worked.seconds))}).toFormat("hh:mm:ss")})</span>
      }
    },
    {
      key: "stopped",
      name: t("stopped"),
      width: 160,
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        let correctWithMaintenanceLoad = p.row.equipment.attributes.correct_maintenance_load
        return (
          <Popover>
            <PopoverTrigger className="hover:text-primary">
              {(p.row.availability.stopped.percent * 100).toFixed(2)}% ({p.row.availability.stopped.time})
            </PopoverTrigger>
            <PopoverContent align="start" alignOffset={-8} className="text-sm w-max ">
              <div className="flex flex-col space-y-2">
                <div className="space-y-1">
                  <div className="space-x-2 flex justify-between">
                    <span className="font-medium">{t('total_stopped')}:</span>
                    <span>{(p.row.availability.stopped.percent * 100).toFixed(2)} ({p.row.availability.stopped.time})</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="space-x-2 flex justify-between">
                    <span className="font-medium">{t('stopped')}:</span>
                    <span>{(p.row.works.stopped.percent * 100).toFixed(2)} ({p.row.works.stopped.time})</span>
                  </div>
                </div>
                <div className="space-y-1">
                  {correctWithMaintenanceLoad ?
                    <div className="space-x-2 flex justify-between">
                      <span className="font-medium">{t('maintenance_stopped')}:</span>
                      <span>{(p.row.maintenance.stopped.percent * 100).toFixed(2)} ({p.row.maintenance.stopped.time})</span>
                    </div> 
                    :
                    <div className="space-x-2 flex justify-between">
                      <span className="font-medium">{t('maintenance_stopped')}:</span>
                      <span>{(p.row.maintenance.on.percent * 100).toFixed(2)} ({p.row.maintenance.on.time})</span>
                    </div> 
                  }
                </div>
              </div>
            </PopoverContent>
          </Popover>
        );
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{(_.sum(p.childRows.map((r) => r.availability.stopped.percent * 100)) / p.childRows.length).toFixed(2)}% ({Duration.fromObject({ seconds: _.sum(p.childRows.map((r) => r.availability.stopped.seconds)) }).toFormat("hh:mm:ss")})</span>
      },
    },
    {
      key: "turnedOff",
      name: t("turned_off"),
      width: 160,
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return (
          <Popover>
            <PopoverTrigger className="hover:text-primary">
              {(p.row.availability.off.percent * 100).toFixed(2)}% ({p.row.availability.off.time})
            </PopoverTrigger>
            <PopoverContent align="start" alignOffset={-8} className="text-sm w-max">
              <div className="flex flex-col space-y-2">
                <div className="space-y-1">
                  <div className="space-x-2 flex justify-between">
                    <span className="font-medium">{t('total_turned_off')}:</span>
                    <span>{(p.row.availability.off.percent * 100).toFixed(2)} ({p.row.availability.off.time})</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="space-x-2 flex justify-between">
                    <span className="font-medium">{t('turned_off')}:</span>
                    <span>{(p.row.works.off.percent * 100).toFixed(2)} ({p.row.works.off.time})</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="space-x-2 flex justify-between">
                    <span className="font-medium">{t('maintenance_turned_off')}:</span>
                    <span>{(p.row.maintenance.off.percent * 100).toFixed(2)} ({p.row.maintenance.off.time})</span>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        );
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{(_.sum(p.childRows.map((r) => r.availability.off.percent * 100)) / p.childRows.length).toFixed(2)}% ({Duration.fromObject({ seconds: _.sum(p.childRows.map((r) => r.availability.off.seconds)) }).toFormat("hh:mm:ss")})</span>
      },
    },
    {
      key: "unavailability",
      name: t("unavailability"),
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return (
          <Popover>
            <PopoverTrigger className="hover:text-primary">
              {(p.row.unavailability.total.percent * 100).toFixed(2)}% ({p.row.unavailability.total.time})
            </PopoverTrigger>
            <PopoverContent align="start" alignOffset={-8} className="text-sm w-max ">
              <div className="flex flex-col space-y-2">
                <div className="space-x-2 flex justify-between">
                  <span>{t('unavailability')}:</span>
                  <span>{(p.row.unavailability.total.percent * 100).toFixed(2)} ({p.row.unavailability.total.time})</span>
                </div>
                <div className="space-x-2 flex justify-between">
                  <span>{t('unavailability_on')}:</span>
                  <span>{(p.row.unavailability.on.percent * 100).toFixed(2)} ({p.row.unavailability.on.time})</span>
                </div>
                <div className="space-x-2 flex justify-between">
                  <span>{t('unavailability_off')}:</span>
                  <span>{(p.row.unavailability.off.percent * 100).toFixed(2)} ({p.row.unavailability.off.time})</span>
                </div>
                <div className="space-x-2 flex justify-between">
                  <span>{t('broken')}:</span>
                  <span>{(p.row.broken.total.percent * 100).toFixed(2)} ({p.row.broken.total.time})</span>
                </div>
                <div className="space-x-2 flex justify-between">
                  <span>{t('repair')}:</span>
                  <span>{(p.row.repair.total.percent * 100).toFixed(2)} ({p.row.repair.total.time})</span>
                </div>
                <div className="space-x-2 flex justify-between">
                  <span>{t('service')}:</span>
                  <span>{(p.row.service.total.percent * 100).toFixed(2)} ({p.row.service.total.time})</span>
                </div>
                <div className="space-x-2 flex justify-between">
                  <span>{t('other')}:</span>
                  <span>{(p.row.other.total.percent * 100).toFixed(2)} ({p.row.other.total.time})</span>
                </div>
                <div className="space-x-2 flex justify-between">
                  <span>{t('interaction')}:</span>
                  <span>{(p.row.unavailability.interaction.percent * 100).toFixed(2)} ({p.row.unavailability.interaction.time})</span>
                </div>
              </div>
            </PopoverContent>
          </Popover>

        );
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{
          Duration.fromObject({ seconds: _.sum(p.childRows.map((r) => r.unavailability.total.seconds)) }).toFormat(
            "hh:mm:ss"
          ) as string
        }</span>
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
    },
    {
      key: "timeline",
      name: t("timeline"),
      width: 180,
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return (
          <TableEventsHistogram height={30} width={176} data={p.row.events} correctMaintenanceLoad={p.row.equipment.attributes.correct_maintenance_load}/>
        )
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => { return <SummaryRows {...p} /> },
    },
    {
      key: "interactions",
      name: t("interactions"),
      resizable: true,
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return <PersonnelRenderCell row={p.row} persons={persons!}/>;
      },
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{
          _.uniq(
            p.childRows
              .map((r) => r.interactions?.filter(i => i.personnel_id > 0).map((i) => {
                let person = persons?.find(p => Number(p.id) === Number(i.personnel_id))
                return `${person?.attributes.last_name} ${person?.attributes?.first_name}`
              }))
              .filter((x) => x !== undefined)
              .flat()
          ).join(", ") as string
        }</span>
      },
    },
    {
      key: "idles",
      name: t("idles"),
      width: 160,
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        let correctWithMaintenanceLoad = p.row.equipment.attributes.correct_maintenance_load
        let workedIdles = p.row.events.filter(e => Number(e[9]) === 0 && e[8] !== "worked" && Number(e[7]) >= 600)
        let maintenaces: Event[] = []
        if(correctWithMaintenanceLoad) {
          let maintenanceIdles = p.row.events.filter(e => Number(e[9]) === 6 && e[8] !== "worked" && Number(e[7]) >= 600)
          workedIdles = [...workedIdles, ...maintenanceIdles]
        } else {
          let eventsByStatus = p.row.events.filter(e => Number(e[14]) === 1)
          eventsByStatus.map((e, eind) => {
            if(Number(e[9]) === 6) {
              let afterEvent = eventsByStatus[eind + 1]
              return maintenaces.push([e[0], e[1], e[2], e[3], e[4], afterEvent[3], afterEvent[4], Number(afterEvent[4]) - Number(e[4]), e[8], e[9], e[10], e[11], e[12], e[13], e[14], e[15]])
            } else {
              return maintenaces
            }
          })
        }
        let sumIdlesMoreThenTenMinutes = correctWithMaintenanceLoad ? _.sum(workedIdles.map(m => m[7])) : _.sum(workedIdles.map(m => m[7])) + (_.sum(maintenaces.map(m => m[7])))
        let sumIdlesLessThenTenMinutes = correctWithMaintenanceLoad ? 
          _.sum(
            p.row.events.filter(e => [0,6].includes(Number(e[9])) && e[8] !== "worked" && Number(e[7]) < 600)
            .map(e => Number(e[7]))
          ) : _.sum(p.row.events.filter(e => Number(e[9]) === 0 && e[8] !== "worked" && Number(e[7]) < 600).map(e => Number(e[7])))
        let totalIdles = p.row.availability.stopped.seconds + p.row.availability.off.seconds 

        return (
          workedIdles && workedIdles.length > 0 &&
          <Popover>
            <PopoverTrigger className="hover:text-primary">
              {t('idles', {count: workedIdles.length})}
            </PopoverTrigger>
            <PopoverContent align="start" alignOffset={-8} className="text-sm w-max ">
              <div className="flex flex-col space-y-2">
                <div>{t('idles_more_then_10_minutes')}</div>
                <div className="max-h-[240px] overflow-scroll space-y-1">
                  {workedIdles.map((idle, idx) => (
                    <div key={idx} className="flex">
                      <span className="w-16">{DateTime.fromSQL(String(idle[3])).toFormat("HH:mm:ss")}</span> 
                      <span className="w-16">{DateTime.fromSQL(String(idle[5])).toFormat("HH:mm:ss")}</span>
                      <span className="w-24">({Duration.fromObject({seconds: Number(idle[7])}).toFormat("hh:mm:ss")})</span>
                      <span className="w-24">{t(`${idle[8]}`)}</span>
                      <span className="w-36 text-muted-foreground">{t(`idle_reason`)}</span>
                    </div>
                  ))}
                  {maintenaces && maintenaces.length > 0 && maintenaces.map((midle, midx) => (
                    <div key={midx} className="flex w-120">
                      <span className="w-16">{DateTime.fromSQL(String(midle[3])).toFormat("HH:mm:ss")}</span>
                      <span className="w-16">{DateTime.fromSQL(String(midle[5])).toFormat("HH:mm:ss")}</span> 
                      <span className="w-24">({Duration.fromObject({seconds: Number(midle[7])}).toFormat("hh:mm:ss")})</span>
                      <span className="w-24"> - </span>
                      <span className="w-36 text-muted-foreground">{t('maintenance')}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col">
                  <div className="flex justify-between">
                    <span>{t('total_idles_more_then_10_minutes')}</span> <span>{Duration.fromObject({seconds: sumIdlesMoreThenTenMinutes}).toFormat("hh:mm:ss")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('total_idles_less_then_10_minutes')}</span> <span>{Duration.fromObject({seconds: sumIdlesLessThenTenMinutes}).toFormat("hh:mm:ss")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('total_idles')}</span> <span>{Duration.fromObject({seconds: totalIdles}).toFormat("hh:mm:ss")}</span>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )
      },
    },
    {
      key: "equipmentStartWork",
      name: t("equipmentStartedAt"),
      width: 160,
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return <>{p.row.violations.startWork.seconds && p.row.violations.startWork.eventTime ? `${DateTime.fromSQL(p.row.violations.startWork.eventTime).toFormat("HH:mm:ss")}` : ""}</>;
      }
    },
    {
      key: "equipmentStartViolation",
      name: t("equipmentStartViolation"),
      width: 160,
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return (
          <>
             {p.row.violations.startWork.seconds ? p.row.violations.startWork.time : ""}
          </>
        );
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{
          Duration.fromObject({ seconds: _.sum(p.childRows.map((r) => r.violations.startWork.seconds)) }).toFormat(
            "hh:mm:ss"
          ) as string
        }</span>
      },
    },
    {
      key: "equipmentEndedAt",
      name: t("equipmentEndedAt"),
      width: 160,
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return <>{p.row.violations.endWork.seconds && p.row.violations.endWork.eventTime ? `${DateTime.fromSQL(p.row.violations.endWork.eventTime).toFormat("HH:mm:ss")}` : ""}</>;
      },
    },
    {
      key: "equipmentEndViolation",
      name: t("equipmentEndViolation"),
      width: 160,
      renderSummaryCell: (p: RenderSummaryCellProps<any, DataRow>) => {
        return <SummaryRows {...p} />;
      },
      renderCell: (p: RenderCellProps<DataRow, any>) => {
        return (
          <>
            {p.row.violations.endWork.seconds ? p.row.violations.endWork.time : ""}
          </>
        );
      },
      renderGroupCell(p: RenderGroupCellProps<DataRow, any>) {
        return <span className="font-medium">{
          Duration.fromObject({ seconds: _.sum(p.childRows.map((r) => r.violations.endWork.seconds)) }).toFormat(
            "hh:mm:ss"
          ) as string
        }</span>
      },
    },
  ];


  useEffect(() => {
    setViewableColumns(columns.filter(col => !hiddenColumns.includes(col.key)))
  }, [hiddenColumns])
  
  // FILTERS, GROUPING AND SORTING

  const categories = tab.options?.filters?.categories;
  const filtersColumns = useState<ColumnFiltersState>(
    tab?.options?.filters?.filters ? tab?.options?.filters?.filters : []
  );

  const groupingColumns = [
    { categoryName: "dates", columnId: "at" },
    { categoryName: "shifts", columnId: "shift" },
    { categoryName: "weekdays", columnId: "weekday" },
    { categoryName: "equipments", columnId: "equipment" },
    { categoryName: "equipment_models", columnId: "equipment_model" },
    { categoryName: "equipment_types", columnId: "equipment_type" },
    { categoryName: "division", columnId: "division" }
  ];
  

  const groupByColumns = useState<GroupingState>(
    tab?.options?.filters?.grouping ? tab?.options?.filters?.grouping : []
  );

  const sortingColumns = [
    { categoryName: "date", columnId: "at" },
    { categoryName: "worked", columnId: "worked" },
    { categoryName: "stopped", columnId: "stopped" },
    { categoryName: "turned_off", columnId: "off" },
  ];

  const sortByColumns = useState<SortingState>(tab?.options?.filters?.sorting ? tab?.options?.filters?.sorting : []);
  const excludeWeekendState = useState(
    tab?.options?.filters?.excludeWeekend ? tab?.options?.filters?.excludeWeekend : false
  );

  const filteredRows = useMemo(() => {
    if (!tab?.options?.filters?.filters) {
      return _.orderBy(rows, "dateShift.start_day");
    }
    let filters = {
      at:  tab?.options?.filters?.filters!.find(f => f.id === "at")?.value as string[] | undefined,
      weekday: tab?.options?.filters?.filters!.find(f => f.id === "weekday")?.value as string[] | undefined,
      shifts:  tab?.options?.filters?.filters!.find(f => f.id === "shift")?.value as string[] | undefined,
      equipment_model: tab?.options?.filters?.filters!.find(f => f.id === "equipment_model")?.value as string[] | undefined,
      equipment: tab?.options?.filters?.filters!.find(f => f.id === "equipment")?.value as string[] | undefined,
      equipment_type: tab?.options?.filters?.filters!.find(f => f.id === "equipment_type")?.value as string[] | undefined,
      division:  tab?.options?.filters?.filters!.find(f => f.id === "division")?.value as string[] | undefined,
      worked:  tab?.options?.filters?.filters!.find(f => f.id === "worked")?.value as number | undefined,
      stopped:  tab?.options?.filters?.filters!.find(f => f.id === "stopped")?.value as number | undefined,
    }

    return rows.filter((r) => {
      return (
        (filters.at !== undefined ? filters.at.includes(r.dateShift.start_day.toISODate()!) : true) &&
        (filters.weekday !== undefined ? filters.weekday.includes(r.dateShift.start_day.weekdayShort!.toLowerCase()) : true) &&
        (filters.shifts !== undefined ? filters.shifts.includes(r.dateShift.id) : true) &&
        (filters.worked !== undefined ? r.availability.worked.seconds >= filters.worked : true) &&
        (filters.stopped !== undefined ? r.availability.stopped.seconds >= filters.stopped : true) &&
        (filters.equipment_model !== undefined ? filters.equipment_model.includes(String(r.equipment.attributes.equipment_model_name)!) : true) &&
        (filters.equipment !== undefined ? filters.equipment.includes(r.equipment.id) : true) &&
        (filters.equipment_type !== undefined ? filters.equipment_type.includes(String(r.equipment.attributes.equipment_type_id)!) : true) &&
        (filters.division !== undefined ? filters.division.includes(String(r.equipment.attributes.division_id)!) : true)
      );
    });
  }, [rows, tab?.options?.filters]);

  const finalRows = useMemo((): readonly DataRow[] => {
    if (!sortByColumns || sortByColumns[0].length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortByColumns[0]) {
        const comparator = getComparator(sort.id);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.desc ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortByColumns]);

  // СВОДНЫЙ РЯД

  const summaryRows = useMemo((): readonly SummaryRow[] => {
    let filterdForSummary = selectedRows.size > 0 ? filteredRows.filter((r) => Array.from(selectedRows).includes(r.id)) : filteredRows;
    return [
      {
        id: "total",
        totalCount: filterdForSummary.length,
        at: _.uniqBy(filterdForSummary, "dateShift.start_day.ts").length,
        weekday:  _.uniqBy(filterdForSummary, "dateShift.start_day.weekday").length,
        shift: _.uniqBy(filterdForSummary, "dateShift.id").length,
        equipment: _.uniqBy(filterdForSummary, "equipment.id").length,
        equipment_model: _.uniqBy(filterdForSummary, "equipment.attributes.equipment_model_id").length,
        equipment_type: _.uniqBy(filterdForSummary, "equipment.attributes.equipment_type_id").length,
        equipment_division: _.uniqBy(filterdForSummary, "equipment.attributes.division_id").length,
        availability: `${(_.sum(filterdForSummary.map(r => r.availability.total.seconds)) / (_.sum(filterdForSummary.map(r => r.duration.total.seconds))) * 100).toFixed(2)}% (${Duration.fromObject({seconds: _.sum(filterdForSummary.map(r => r.availability.total.seconds))}).toFormat("hh:mm:ss")})`,
        worked: `${((_.sum(filterdForSummary.map(r => r.availability.worked.seconds)) / (_.sum(filterdForSummary.map(r => r.availability.worked.seconds)) + _.sum(filterdForSummary.map(r => r.availability.stopped.seconds)) + _.sum(filterdForSummary.map(r => r.availability.off.seconds)))) * 100).toFixed(2)}% (${Duration.fromObject({seconds: _.sum(filterdForSummary.map(r => r.availability.worked.seconds))}).toFormat("hh:mm:ss")})`,
        stopped: `${((_.sum(filterdForSummary.map(r => r.availability.stopped.seconds)) / (_.sum(filterdForSummary.map(r => r.availability.worked.seconds)) + _.sum(filterdForSummary.map(r => r.availability.stopped.seconds)) + _.sum(filterdForSummary.map(r => r.availability.off.seconds)))) * 100).toFixed(2)}% (${Duration.fromObject({seconds: _.sum(filterdForSummary.map(r => r.availability.stopped.seconds))}).toFormat("hh:mm:ss")})`,
        turnedOff: `${((_.sum(filterdForSummary.map(r => r.availability.off.seconds)) / (_.sum(filterdForSummary.map(r => r.availability.worked.seconds)) + _.sum(filterdForSummary.map(r => r.availability.stopped.seconds)) + _.sum(filterdForSummary.map(r => r.availability.off.seconds)))) * 100).toFixed(2)}% (${Duration.fromObject({seconds: _.sum(filterdForSummary.map(r => r.availability.off.seconds))}).toFormat("hh:mm:ss")})`,
        unavailability: `${(_.sum(filterdForSummary.map(r => r.unavailability.total.seconds)) / (_.sum(filterdForSummary.map(r => r.duration.total.seconds))) * 100).toFixed(2)}% (${Duration.fromObject({seconds: _.sum(filterdForSummary.map(r => r.unavailability.total.seconds))}).toFormat("hh:mm:ss")})`,
        equipmentStartViolation: `${Duration.fromObject({seconds: _.sum(filterdForSummary.map(r => r.violations.startWork.seconds))}).toFormat("hh:mm:ss")}`,
        equipmentEndViolation: `${Duration.fromObject({seconds: _.sum(filterdForSummary.map(r => r.violations.endWork.seconds))}).toFormat("hh:mm:ss")}`,
      }
    ];
  }, [filteredRows, selectedRows]);



  return (
    <>  
      <div className="flex flex-col w-full h-full">     
        <div className="flex flex-none h-12 w-full items-center px-2 gap-4 justify-between">
          <div className="flex gap-2 items-center">
            <ReportViewButton />
          </div>
          <div className="flex gap-2">
            <Columns columns={columns} hiddenColumns={hiddenColumns} onChange={setHiddenColumns}/>
            <EquipmentsFilters
              filtersState={filtersColumns}
              excludeWeekendState={excludeWeekendState}
              tab={tab}
              categories={categories}
            />
            <Grouping groupingState={groupByColumns} tab={tab} groupingColumns={groupingColumns}/>
            <Sorting sortingState={sortByColumns} tab={tab} sortingColumns={sortingColumns}/>
            <CSVLink data={getCSVData(rows, persons!)} filename={"report.csv"} separator={";"}>
              <Button variant="outline" size="datepicker" className="gap-2"><TbCsv /> {t('save_to_csv')}</Button>
            </CSVLink>
          </div>
        </div>
        <Separator className="flex-none" />
        <ReportTableDataGrid
          columns={viewableColumns}
          rows={finalRows}
          summaryRows={summaryRows}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          groupByColumns={groupByColumns[0]}
          sortByColumns={sortByColumns[0]}
        />
        {showGraph && showGraphForRow ? (
          <GraphRender row={rows.find((r) => r.id === showGraphForRow)!} close={handleCloseShowGraph} />
        ) : (
          <></>
        )}
      </div>
    </>
    
  );
}

export default TableView;

function PersonnelRenderCell({row, persons}:{row:DataRow, persons: Person[]}) {
  let interactions = row.interactionIds.filter(i => i > 0)
  let personsIteractions = row.interactions.filter(pi => pi.personnel_id > 0)
  let uniqPersonsCount = interactions.length

  function getPerson(id: number) {
    let person = persons.find(p => Number(p.id) === id)
    return `${person?.attributes.last_name} ${person?.attributes.first_name} ${person?.attributes.middle_name}`
  }

  return (
    <div className="flex items-center gap-2 w-full h-full py-1.5">
      {interactions && interactions.length > 0 && 
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="flex-none flex w-full hover:text-primary truncate text-left">
              {uniqPersonsCount === 1 ? 
                <span className="w-full truncate">{getPerson(interactions[0])}</span> 
                : 
                <span className="w-full lowercase truncate">{t('person', {count: uniqPersonsCount})}</span>
              }
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" alignOffset={-8} className="text-sm flex flex-col gap-2">
            {_.orderBy(personsIteractions, "start").map((item, idx) => (
              <div key={idx}>
                <div className="font-semibold">{getPerson(item.personnel_id)}</div>
                <div>
                  {DateTime.fromSQL(String(item.from)).toFormat("HH:mm:ss")} - {DateTime.fromSQL(String(item.to)).toFormat("HH:mm:ss")}
                  <div className="grid grid-cols-3">
                    <div>{t('total')}</div>
                    <div>{t('worked')}</div>
                    <div>{t('stopped')}</div>
                    <div>{Duration.fromMillis(item.duration * 1000).toFormat("hh:mm:ss")}</div>
                    <div>{Duration.fromMillis(item.worked * 1000).toFormat("hh:mm:ss")}</div>
                    <div>{Duration.fromMillis(item.stopped * 1000).toFormat("hh:mm:ss")}</div>
                  </div>
                </div>
              </div>
            ))}
          </PopoverContent>
        </Popover>
      }
    </div>
  )
}

function GraphRender({row, close}: {row: DataRow, close: () => void;}) {
  const {equipments} = useAppStore()
  const equipment = equipments?.find(e => e.id === row.equipment.id)!
  const dates = {from: row.dateShift.start_time.toJSDate(), to: row.dateShift.end_time.toJSDate()}
  return (
    <div className="flex flex-col h-96 w-full">
      <div className="flex w-full h-6 text-sm items-center px-2 justify-between">
        <div className="truncate space-x-2">
          <span>{row.dateShift.start_day.toFormat("dd.MM.yyyy")}</span>
          <span>{row.dateShift.start_day.weekdayShort?.toLocaleUpperCase()}</span>
          <span>{row.dateShift.label}</span>
          <span>{row.equipment.attributes.name}</span>
        </div>
        <TbX onClick={close}/>
      </div>
      <Separator />
      <div className="pt-4">
        <div className="px-8">
          {/* <ShiftTimeline
            data={{ at: row.at, shift: row.shift, timeline: row.timeline! }}
            height={30}
            width={"100%"}
          /> */}
        </div>
        <div className="px-6">
          {equipment.attributes.sensor_ids.map(sensorId => (
            <DeviceSensorDataInReport key={sensorId} sensorId={sensorId} selectedDate={dates} equipmentId={equipment.id}/>
          ))}
        </div>
      </div>
    </div>
  )
}

function getCSVData(rows: DataRow[], persons: Person[]) {
    // CSV
    return [
      [
        "ID",
        t("at"),
        t("weekday"),
        t("equipment_name"),
        t("inventory_number"),
        t("shift"),
        t("duration.total"),
        t("duration.total"),
        t("duration.on"),
        t("duration.on"),
        t("duration.off"),
        t("duration.off"),
        t("duration.worked"),
        t("duration.worked"),
        t("duration.stopped"),
        t("duration.stopped"),
        t("duration.interaction"),
        t("duration.interaction"),
        t("availability.total"),
        t("availability.total"),
        t("availability.total"),
        t("availability.on"),
        t("availability.on"),
        t("availability.on"),
        t("availability.off"),
        t("availability.off"),
        t("availability.off"),
        t("availability.worked"),
        t("availability.worked"),
        t("availability.worked"),
        t("availability.stopped"),
        t("availability.stopped"),
        t("availability.stopped"),
        t("availability.interaction"),
        t("availability.interaction"),
        t("availability.interaction"),
        t("works.total"),
        t("works.total"),
        t("works.total"),
        t("works.on"),
        t("works.on"),
        t("works.on"),
        t("works.off"),
        t("works.off"),
        t("works.off"),
        t("works.worked"),
        t("works.worked"),
        t("works.worked"),
        t("works.stopped"),
        t("works.stopped"),
        t("works.stopped"),
        t("works.interaction"),
        t("works.interaction"),
        t("works.interaction"),
        t("maintenance.total"),
        t("maintenance.total"),
        t("maintenance.total"),
        t("maintenance.on"),
        t("maintenance.on"),
        t("maintenance.on"),
        t("maintenance.off"),
        t("maintenance.off"),
        t("maintenance.off"),
        t("maintenance.worked"),
        t("maintenance.worked"),
        t("maintenance.worked"),
        t("maintenance.stopped"),
        t("maintenance.stopped"),
        t("maintenance.stopped"),
        t("maintenance.interaction"),
        t("maintenance.interaction"),
        t("maintenance.interaction"),
        t("unavailability.total"),
        t("unavailability.total"),
        t("unavailability.total"),
        t("unavailability.on"),
        t("unavailability.on"),
        t("unavailability.on"),
        t("unavailability.off"),
        t("unavailability.off"),
        t("unavailability.off"),
        t("unavailability.worked"),
        t("unavailability.worked"),
        t("unavailability.worked"),
        t("unavailability.stopped"),
        t("unavailability.stopped"),
        t("unavailability.stopped"),
        t("unavailability.interaction"),
        t("unavailability.interaction"),
        t("unavailability.interaction"),
        t("broken.total"),
        t("broken.total"),
        t("broken.total"),
        t("broken.on"),
        t("broken.on"),
        t("broken.on"),
        t("broken.off"),
        t("broken.off"),
        t("broken.off"),
        t("broken.worked"),
        t("broken.worked"),
        t("broken.worked"),
        t("broken.stopped"),
        t("broken.stopped"),
        t("broken.stopped"),
        t("broken.interaction"),
        t("broken.interaction"),
        t("broken.interaction"),
        t("repair.total"),
        t("repair.total"),
        t("repair.total"),
        t("repair.on"),
        t("repair.on"),
        t("repair.on"),
        t("repair.off"),
        t("repair.off"),
        t("repair.off"),
        t("repair.worked"),
        t("repair.worked"),
        t("repair.worked"),
        t("repair.stopped"),
        t("repair.stopped"),
        t("repair.stopped"),
        t("repair.interaction"),
        t("repair.interaction"),
        t("repair.interaction"),
        t("service.total"),
        t("service.total"),
        t("service.total"),
        t("service.on"),
        t("service.on"),
        t("service.on"),
        t("service.off"),
        t("service.off"),
        t("service.off"),
        t("service.worked"),
        t("service.worked"),
        t("service.worked"),
        t("service.stopped"),
        t("service.stopped"),
        t("service.stopped"),
        t("service.interaction"),
        t("service.interaction"),
        t("service.interaction"),
        t("other.total"),
        t("other.total"),
        t("other.total"),
        t("other.on"),
        t("other.on"),
        t("other.on"),
        t("other.off"),
        t("other.off"),
        t("other.off"),
        t("other.worked"),
        t("other.worked"),
        t("other.worked"),
        t("other.stopped"),
        t("other.stopped"),
        t("other.stopped"),
        t("other.interaction"),
        t("other.interaction"),
        t("other.interaction"),
        t("violations.start"),
        t("violations.start"),
        t("violations.start"),
        t("violations.start"),
        t("violations.startInteraction"),
        t("violations.startInteraction"),
        t("violations.startInteraction"),
        t("violations.startInteraction"),
        t("violations.startWork"),
        t("violations.startWork"),
        t("violations.startWork"),
        t("violations.startWork"),
        t("violations.end"),
        t("violations.end"),
        t("violations.end"),
        t("violations.end"),
        t("violations.endInteraction"),
        t("violations.endInteraction"),
        t("violations.endInteraction"),
        t("violations.endInteraction"),
        t("violations.endWork"),
        t("violations.endWork"),
        t("violations.endWork"),
        t("violations.endWork"),
        t("interactions_count"),
        t("interactions_with")
      ],
      ...rows!.map((row) => [
        row.id,
        row.dateShift.start_day.toSQLDate(),
        row.dateShift.start_day.weekdayLong,
        row.equipment.attributes.name,
        row.equipment.attributes.inventory_number,
        row.dateShift.label,
        row.duration.total.time,
        row.duration.total.seconds,
        row.duration.on.time,
        row.duration.on.seconds,
        row.duration.off.time,
        row.duration.off.seconds,
        row.duration.worked.time,
        row.duration.worked.seconds,
        row.duration.stopped.time,
        row.duration.stopped.seconds,
        row.duration.interaction.time,
        row.duration.interaction.seconds,
        row.availability.total.time,
        row.availability.total.seconds,
        row.availability.total.percent * 100,
        row.availability.on.time,
        row.availability.on.seconds,
        row.availability.on.percent * 100,
        row.availability.off.time,
        row.availability.off.seconds,
        row.availability.off.percent * 100,
        row.availability.worked.time,
        row.availability.worked.seconds,
        row.availability.worked.percent * 100,
        row.availability.stopped.time,
        row.availability.stopped.seconds,
        row.availability.stopped.percent * 100,
        row.availability.interaction.time,
        row.availability.interaction.seconds,
        row.availability.interaction.percent * 100,
        row.works.total.time,
        row.works.total.seconds,
        row.works.total.percent * 100,
        row.works.on.time,
        row.works.on.seconds,
        row.works.on.percent * 100,
        row.works.off.time,
        row.works.off.seconds,
        row.works.off.percent * 100,
        row.works.worked.time,
        row.works.worked.seconds,
        row.works.worked.percent * 100,
        row.works.stopped.time,
        row.works.stopped.seconds,
        row.works.stopped.percent * 100,
        row.works.interaction.time,
        row.works.interaction.seconds,
        row.works.interaction.percent * 100,
        row.maintenance.total.time,
        row.maintenance.total.seconds,
        row.maintenance.total.percent * 100,
        row.maintenance.on.time,
        row.maintenance.on.seconds,
        row.maintenance.on.percent * 100,
        row.maintenance.off.time,
        row.maintenance.off.seconds,
        row.maintenance.off.percent * 100,
        row.maintenance.worked.time,
        row.maintenance.worked.seconds,
        row.maintenance.worked.percent * 100,
        row.maintenance.stopped.time,
        row.maintenance.stopped.seconds,
        row.maintenance.stopped.percent * 100,
        row.maintenance.interaction.time,
        row.maintenance.interaction.seconds,
        row.maintenance.interaction.percent * 100,
        row.unavailability.total.time,
        row.unavailability.total.seconds,
        row.unavailability.total.percent * 100,
        row.unavailability.on.time,
        row.unavailability.on.seconds,
        row.unavailability.on.percent * 100,
        row.unavailability.off.time,
        row.unavailability.off.seconds,
        row.unavailability.off.percent * 100,
        row.unavailability.worked.time,
        row.unavailability.worked.seconds,
        row.unavailability.worked.percent * 100,
        row.unavailability.stopped.time,
        row.unavailability.stopped.seconds,
        row.unavailability.stopped.percent * 100,
        row.unavailability.interaction.time,
        row.unavailability.interaction.seconds,
        row.unavailability.interaction.percent * 100,
        row.broken.total.time,
        row.broken.total.seconds,
        row.broken.total.percent * 100,
        row.broken.on.time,
        row.broken.on.seconds,
        row.broken.on.percent * 100,
        row.broken.off.time,
        row.broken.off.seconds,
        row.broken.off.percent * 100,
        row.broken.worked.time,
        row.broken.worked.seconds,
        row.broken.worked.percent * 100,
        row.broken.stopped.time,
        row.broken.stopped.seconds,
        row.broken.stopped.percent * 100,
        row.broken.interaction.time,
        row.broken.interaction.seconds,
        row.broken.interaction.percent * 100,
        row.repair.total.time,
        row.repair.total.seconds,
        row.repair.total.percent * 100,
        row.repair.on.time,
        row.repair.on.seconds,
        row.repair.on.percent * 100,
        row.repair.off.time,
        row.repair.off.seconds,
        row.repair.off.percent * 100,
        row.repair.worked.time,
        row.repair.worked.seconds,
        row.repair.worked.percent * 100,
        row.repair.stopped.time,
        row.repair.stopped.seconds,
        row.repair.stopped.percent * 100,
        row.repair.interaction.time,
        row.repair.interaction.seconds,
        row.repair.interaction.percent * 100,
        row.service.total.time,
        row.service.total.seconds,
        row.service.total.percent * 100,
        row.service.on.time,
        row.service.on.seconds,
        row.service.on.percent * 100,
        row.service.off.time,
        row.service.off.seconds,
        row.service.off.percent * 100,
        row.service.worked.time,
        row.service.worked.seconds,
        row.service.worked.percent * 100,
        row.service.stopped.time,
        row.service.stopped.seconds,
        row.service.stopped.percent * 100,
        row.service.interaction.time,
        row.service.interaction.seconds,
        row.service.interaction.percent * 100,
        row.other.total.time,
        row.other.total.seconds,
        row.other.total.percent * 100,
        row.other.on.time,
        row.other.on.seconds,
        row.other.on.percent * 100,
        row.other.off.time,
        row.other.off.seconds,
        row.other.off.percent * 100,
        row.other.worked.time,
        row.other.worked.seconds,
        row.other.worked.percent * 100,
        row.other.stopped.time,
        row.other.stopped.seconds,
        row.other.stopped.percent * 100,
        row.other.interaction.time,
        row.other.interaction.seconds,
        row.other.interaction.percent * 100,
        row.violations.start.eventTime,
        row.violations.start.time,
        row.violations.start.seconds,
        row.violations.start.percent * 100,
        row.violations.startInteraction.eventTime,
        row.violations.startInteraction.time,
        row.violations.startInteraction.seconds,
        row.violations.startInteraction.percent * 100,
        row.violations.startWork.eventTime,
        row.violations.startWork.time,
        row.violations.startWork.seconds,
        row.violations.startWork.percent * 100,
        row.violations.end.eventTime,
        row.violations.end.time,
        row.violations.end.seconds,
        row.violations.end.percent * 100,
        row.violations.endInteraction.eventTime,
        row.violations.endInteraction.time,
        row.violations.endInteraction.seconds,
        row.violations.endInteraction.percent * 100,
        row.violations.endWork.eventTime,
        row.violations.endWork.time,
        row.violations.endWork.seconds,
        row.violations.endWork.percent * 100,
        row.interactionIds.filter(i => i > 0).length,
        row.interactionIds.filter(i => i > 0).map(i => {
          let person = persons.find(p => Number(p.id) === i)
          return `${person?.attributes.last_name} ${person?.attributes.first_name} ${person?.attributes.middle_name}`
        }).join(", ")
      ]),
    ];
}